// Job List Card Component - Shared between Jobs and PublicJobs screens
import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { MapPin, Calendar } from 'lucide-react';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { webColors } from '../../../constants/webStyles';
import { jobTypes } from '../../../constants/lov/professions';
import { jobCardStyles as styles } from './jobStyles';
import { getColorFromName, getTextColorFromName, getJobTypeBgColor, getJobTypeColor } from './jobHelpers';
import { formatPrice, formatPriceRange, getJobDisplayCurrency } from '../../../utils/currency';
import { getWorldCountryData } from '../../../constants/worldCountries';
import { getOverview } from '../../../services/profile';

/**
 * Job List Card Component (Left Panel)
 * @param {Object} props
 * @param {Object} props.job - Job data object
 * @param {boolean} props.isSelected - Whether this card is currently selected
 * @param {boolean} [props.isSaved] - Whether this job is saved (optional, for authenticated users)
 * @param {Function} props.onPress - Callback when card is pressed
 */
const JobListCard = ({ job, isSelected, isSaved, onPress }) => {
  const { data: overviewData } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
  });
  const clinicCity = overviewData?.data?.data?.city;
  const clinicState = overviewData?.data?.data?.state;

  const getJobType = () => {
    if (job.type !== undefined && job.type !== null) {
      const typeObj = jobTypes.find(t => t.id === parseInt(job.type));
      return typeObj?.name || 'Locum';
    }
    return job.is_locum ? 'Locum' : 'Full-Time';
  };

  const isLocum = job.type === '0' || job.type === 0;
  const currency = getJobDisplayCurrency(job);

  const formatSalary = () => {
    // For locum jobs, show rate per hour and total price
    if (isLocum && job.rate_per_hour && job.price) {
      return `${formatPrice(job.rate_per_hour, currency)}/hr • ${formatPrice(job.price, currency)}`;
    }
    if (job.price && job.price_max && Number(job.price_max) > Number(job.price)) {
      return formatPriceRange(job.price, job.price_max, currency);
    }
    if (job.price) return formatPrice(job.price, currency);
    if (job.salary_min && job.salary_max) return formatPriceRange(job.salary_min, job.salary_max, currency);
    return 'Competitive';
  };

  const formatSchedule = () => {
    if (!isLocum || !job.start_timestamp || !job.end_timestamp) return null;
    const start = moment(job.start_timestamp);
    const end = moment(job.end_timestamp);
    const dayDate = start.format('ddd, D MMM');
    const startTime = start.format('h:mmA').replace(':00', '');
    const endTime = end.format('h:mmA').replace(':00', '');
    return `${dayDate} • ${startTime} – ${endTime}`;
  };

  // Non-locum jobs (full-time, temporary, contract, international) have no shift
  // time, so end_timestamp represents the application deadline (see HomeFacility).
  const formatDeadline = () => {
    if (isLocum || !job.end_timestamp) return null;
    return `Apply by ${moment(job.end_timestamp).format('D MMM')}`;
  };

  const clinicName = job.clinic?.name || job.clinic_name;
  const city = job.clinic?.city || job.city || clinicCity;
  const state = job.clinic?.state || job.state || clinicState;
  const location = city
    ? `${city}${state ? `, ${state}` : ''}`
    : (job.location || 'Location TBD');

  return (
    <TouchableOpacity
      style={[styles.jobCard, isSelected && styles.jobCardSelected]}
      onPress={onPress}
    >
      <View style={styles.jobCardHeader}>
        {job.subtitle ? (
          <View style={styles.jobCardSubtitlePill}>
            <Text style={styles.jobCardSubtitleText}>{job.subtitle}</Text>
          </View>
        ) : null}
        <View style={styles.jobCardTitleRow}>
          <Text style={styles.jobCardTitle} numberOfLines={2}>
            {job.title || 'Job Position'}
          </Text>
          <Text style={styles.jobCardTime}>{moment(job.created_at).fromNow()}</Text>
        </View>
      </View>

      <View style={styles.jobCardCompany}>
        <View style={[styles.jobCardLogo, { backgroundColor: getColorFromName(clinicName) }]}>
          <Text style={styles.jobCardLogoText}>{clinicName?.[0] || 'C'}</Text>
        </View>
        <View style={styles.jobCardCompanyInfo}>
          <Text style={[styles.jobCardCompanyName, { color: getTextColorFromName(clinicName) }]}>
            {clinicName || 'Healthcare Provider'}
          </Text>
          <View style={styles.jobCardLocationRow}>
            <MapPin size={14} color={webColors.textMuted} />
            <Text style={styles.jobCardLocation}>{location}</Text>
          </View>
        </View>
      </View>

      <View style={styles.jobCardTags}>
        <View style={[styles.jobCardTag, { backgroundColor: getJobTypeBgColor(getJobType()) }]}>
          <Text style={[styles.jobCardTagText, { color: getJobTypeColor(getJobType()) }]}>
            {getJobType()}
          </Text>
        </View>
        {job.international && (
          <View style={[styles.jobCardTag, { backgroundColor: '#e0e7ff' }]}>
            <Text style={styles.jobCardTagText}>
              {`${getWorldCountryData(job.international)?.flag || ''} ${getWorldCountryData(job.international)?.name || 'International'}`.trim()}
            </Text>
          </View>
        )}
        {formatSchedule() && (
          <View style={[styles.jobCardTag, styles.jobCardTagRow, styles.jobCardTagSchedule]}>
            <Calendar size={14} color="#c2185b" />
            <Text style={[styles.jobCardTagText, styles.jobCardTagScheduleText]}>{formatSchedule()}</Text>
          </View>
        )}
        {formatDeadline() && (
          <View style={[styles.jobCardTag, styles.jobCardTagRow, styles.jobCardTagDeadline]}>
            <Calendar size={14} color="#92400e" />
            <Text style={[styles.jobCardTagText, styles.jobCardTagDeadlineText]}>{formatDeadline()}</Text>
          </View>
        )}
        <View style={[styles.jobCardTag, styles.jobCardTagRow]}>
          <Text style={styles.jobCardTagText}>{formatSalary()}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default JobListCard;
